import { useCallback, useEffect, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, Stack, Typography } from '@mui/material';
import { useGetUsersQuery, useLazyGetFilteredUsersQuery } from '~/features/user/model/api/users-api';
import { ModalRole } from '~/shared/ui/modals/modalRole/ModalRole';
import Dashboard from '~/features/dashboard/Dashboard';
import { DataTable } from '~/shared/ui/tables/DataTable';
import { defaultPaginationModel, defaultEditedUserModel } from '~/shared/const/app';
import { hasAccess } from '~/shared/config/access';
import { useSelector } from 'react-redux';
import { usersApi } from '~/features/user/model/api/users-api';
import { useSearchParams } from 'react-router-dom';
import { IPaginationModel } from '~/shared/types/types';
import { usePersistedTableState } from '~/shared/hooks/usePersistedTableState';

export const UsersPage = () => {
  const [open, setOpen] = useState(false);
  const [editedUser, setEditedUser] = useState(defaultEditedUserModel);

  const {
    activeFilters,
    setActiveFilters,
    sortModel,
    setSortModel,
    paginationModel,
    setPaginationModel,
  } = usePersistedTableState('users', defaultPaginationModel);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data: me } = useSelector(usersApi.endpoints.getUserInfo.select(undefined)) as any;

  const sortBy = sortModel?.[0]?.field;
  const sortDir = sortModel?.[0]?.sort;

  const { data: responseData, isFetching, refetch } = useGetUsersQuery({
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    search: paginationModel.search,
    sort_by: sortBy,
    sort_dir: sortDir,
  }, { skip: !!activeFilters });

  const [getFilteredUsers, { data: filteredData, isFetching: isFiltering }] = useLazyGetFilteredUsersQuery();
  const filterColumns = useMemo(() => ([
    { key: 'name', label: 'Имя', type: 'multi-text' },
    { key: 'email', label: 'Email', type: 'multi-text' },
    {
      key: 'role',
      label: 'Роль',
      type: 'select',
      options: [
        { label: 'Admin', value: 'admin' },
        { label: 'Buyer', value: 'buyer' },
        { label: 'Farmer', value: 'farmer' },
      ],
    },
  ]), []);

  const defaultUsersFilterState = useMemo(() => ({
    name: '',
    email: '',
    role: '',
  }), []);

  const rows = (isFetching || isFiltering) ? [] : ((activeFilters ? filteredData?.data : responseData?.data) || []);
  const rowCount = (activeFilters ? filteredData?.pagination?.total : responseData?.pagination?.total) || 0;

  useEffect(() => {
    if (activeFilters) {
      getFilteredUsers({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: paginationModel.search,
        filters: activeFilters,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
    }
  }, [paginationModel, activeFilters, sortBy, sortDir]);

  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);
  const handleRoleUpdated = useCallback(() => {
    if (activeFilters) {
      getFilteredUsers({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: paginationModel.search,
        filters: activeFilters,
        sort_by: sortBy,
        sort_dir: sortDir,
      } as any);
      return;
    }
    refetch();
  }, [activeFilters, getFilteredUsers, paginationModel.page, paginationModel.pageSize, paginationModel.search, refetch, sortBy, sortDir]);

  const handleRefresh = useCallback(() => {
    if (activeFilters) {
      getFilteredUsers({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: paginationModel.search,
        filters: activeFilters,
        sort_by: sortBy,
        sort_dir: sortDir,
      } as any);
      return;
    }
    refetch();
  }, [activeFilters, getFilteredUsers, paginationModel.page, paginationModel.pageSize, paginationModel.search, refetch, sortBy, sortDir]);

  const editRole = useCallback(
    (params: any) => {
      setEditedUser({
        id: params.row.id,
        name: params.row.name,
        role: params.row.roles,
      });
      handleOpen();
    },
    []
  );

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 120 },
      { field: 'name', headerName: 'Имя', width: 360, minWidth: 360, flex: 0 },
      { field: 'email', headerName: 'Email', width: 400, minWidth: 400, flex: 0 },
      {
        field: 'created_at',
        headerName: 'Дата создания',
        width: 220,
        renderCell: params => (
          <span>
            {params.value
              ? new Date(params.value).toLocaleString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })
              : 'Не задано'}
          </span>
        ),
      },
      {
        field: 'role',
        headerName: 'Роль',
        width: 140,
        valueGetter: (value: any, row: any) => row.roles?.join(', ') || '',
      },
      {
        field: 'edit',
        headerName: 'Редактировать',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: params => (
          <IconButton size="small" sx={{ color: 'primary.main', p: 0.5, m: 0.25 }} onClick={() => editRole(params)}>
            <EditIcon />
          </IconButton>
        ),
      },
    ],
    [editRole]
  );

  return (
    <Dashboard>
      {
        hasAccess(me?.roles ?? [], '/users') ? (
          <Stack flexDirection="column" gap="16px">
            <Stack direction={{ xs: 'row', lg: 'row' }} sx={{ alignItems: 'center' }}>
              <Typography component="h2" variant="h6" sx={{ flex: 1 }}>
                Пользователи
              </Typography>
            </Stack>
            <DataTable
              rows={rows}
              columns={columns}
              rowCount={rowCount}
              isFetching={isFetching || isFiltering}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              sortModel={sortModel}
              setSortModel={(model: any) => {
                setSortModel(model);
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
              sortingMode="server"
              filterColumns={filterColumns}
              filterDefaultState={defaultUsersFilterState}
              onRefresh={handleRefresh}
              checkboxSelection={false}
              onFilterApply={(filterState: Record<string, any>) => {
                const filters = {
                  ...filterState,
                  name: typeof filterState.name === 'string'
                    ? filterState.name
                      .split('\n')
                      .map((s: string) => s.trim())
                      .filter(Boolean)
                    : Array.isArray(filterState.name)
                      ? filterState.name.filter(Boolean)
                      : [],
                  email: typeof filterState.email === 'string'
                    ? filterState.email
                      .split('\n')
                      .map((s: string) => s.trim())
                      .filter(Boolean)
                    : Array.isArray(filterState.email)
                      ? filterState.email.filter(Boolean)
                      : [],
                };

                setActiveFilters(filters);
                getFilteredUsers({
                  page: paginationModel.page + 1,
                  pageSize: paginationModel.pageSize,
                  search: paginationModel.search,
                  filters,
                  sort_by: sortBy,
                  sort_dir: sortDir,
                } as any);
              }}
              onFilterReset={() => {
                setActiveFilters(null);
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
              activeFilters={activeFilters}
            />
            <ModalRole handleClose={handleClose} user={editedUser} open={open} onUpdated={handleRoleUpdated} />
          </Stack>
        ) : (
          <Stack>Нет доступа</Stack>
        )
      }
    </Dashboard >
  );
};
